import { useCallback, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { READ_SIMULATION_FOR_UNITY } from '@base/queries/LearnerQueries';
import { Button, useStateUrl } from '@virtidev/toolbox';
import { Redirect, useLocation, useParams } from 'react-router-dom';
import PageLoadError from '@base/components/PageLoadError';
import LoadingSplash from '@core/components/VirtiApolloProvider/components/LoadingSplash';
import usePublicToken from '@core/helpers/usePublicToken';
import RegisterBanner from '@core/components/Signup/RegisterBanner/RegisterBanner';
import useUser from '@core/helpers/useUser';
import SimulationPublicPlayer from '@core/components/Unity/components/SimulationPublicPlayer/SimulationPublicPlayer';
import { Simulation } from '@core/models/simulation.types';
import { IsMobile, WeSupportUnity } from '@base/utility/BrowserCheck';
import { MobileMessage } from '@core/pages/admin/virtual-humans/VHWebPlayerPagePublic/VHWebPlayerPagePublic.styled';
import { generateDeepLink } from '@core/components/DeepLink/helpers/generateDeepLink';
import { CHECK_LOGIN } from '@base/queries/UserQueries';
import { useIsIframe } from '@core/helpers/useIsIframe';

export const SimulationPlayerPublicPage = () => {
  const { sharetoken } = useParams();
  const { ID, Organisations } = useUser();
  const { pathname } = useLocation();
  const { value: redirect } = useStateUrl({ key: 'redirect' });
  const embedded = useIsIframe();

  const loggedin = ID && ID !== '0';

  const {
    data: publicData,
    error: publicError,
    loading: publicLoading,
    called,
  } = usePublicToken({ skip: loggedin });

  const { value: querySimID, updateValue: updateSimID } = useStateUrl({
    replace: true,
    key: 'simID',
  });

  const {
    data: loggedinData,
    loading: loggedinLoading,
    error: loggedinError,
  } = useQuery(CHECK_LOGIN, {
    variables: {
      ID,
    },
    skip: !loggedin,
  });

  const { data, loading, error } = useQuery(READ_SIMULATION_FOR_UNITY, {
    variables: {
      sharetokenHeader: sharetoken,
      filter: { ShareToken: { eq: sharetoken } },
    },
    skip: !sharetoken || (called && !publicData),
  });

  /** @type {Simulation | undefined} */
  const simulation = useMemo(() => data?.readOneSimulation, [data]);

  const inOrg = useMemo(
    () =>
      Organisations?.some?.(
        ({ ID: orgId }) => simulation?.OrganisationID === orgId
      ),
    [Organisations, simulation?.OrganisationID]
  );

  const deepLink = useMemo(() => {
    if (!simulation?.ID || redirect === '1' || embedded) {
      return null;
    }
    const search = new URLSearchParams({
      simID: simulation?.ID,
      content: 'true',
      public: 'true',
      organisationID: inOrg ? simulation?.OrganisationID : 'null',
    });

    return generateDeepLink(`${pathname}?${search.toString()}`, null);
  }, [simulation, redirect, embedded, inOrg, pathname]);

  const goToMobileDeepLink = useCallback(() => {
    if (deepLink) {
      window.onbeforeunload = null;

      window.location.href = deepLink;
    }
  }, [deepLink]);

  const shouldRedirectWebapp = !embedded && loggedin && inOrg;

  const showRegisterPrompt = !!simulation?.ShowRegister && !inOrg && !embedded;

  useEffect(() => {
    if (simulation?.ID) {
      updateSimID(simulation?.ID);
    }
  }, [updateSimID, simulation?.ID]);

  if (!WeSupportUnity() || IsMobile()) {
    return (
      <MobileMessage>
        {showRegisterPrompt && (
          <RegisterBanner
            type="simulation"
            sharetoken={sharetoken}
            loggedin={loggedin}
          />
        )}
        <div>
          Unfortunately, web preview is currently only available in latest
          version of Google Chrome, Mozilla Firefox, and Microsoft Edge on a
          computer.
        </div>
        {deepLink && (
          <div>
            <Button color="primary" onClick={goToMobileDeepLink}>
              Use the Virti App
            </Button>
          </div>
        )}
      </MobileMessage>
    );
  }

  if (error || publicError) {
    return <PageLoadError graphQLErrorObj={publicError || error} />;
  }

  if (loading || publicLoading || loggedinLoading) {
    return <LoadingSplash show />;
  }

  if (!simulation || !querySimID || !sharetoken) {
    return null;
  }

  if (shouldRedirectWebapp || loggedinError) {
    const validLogin = loggedinData?.readOneMember?.ID === ID;

    if (validLogin) {
      return <Redirect to={`/my-simulations/${simulation.ID}/${sharetoken}`} />;
    }
    const message = 'Your login had expired, please login again';
    return (
      <Redirect
        to={{
          pathname: '/logout',
          search: `msg=${encodeURIComponent(message)}`,
          state: {
            // bring them back to the content page after logging in
            prevPath: `/my-simulations/${simulation.ID}/${sharetoken}`,
          },
        }}
      />
    );
  }

  if (simulation.ShareLoginRequired) {
    if (!loggedin) {
      const message =
        'An account is required for this content, please logn to continue';
      return (
        <Redirect
          to={{
            pathname: '/logout',
            search: `msg=${encodeURIComponent(message)}`,
            state: {
              // bring them back to the content page after logging in
              prevPath: `/embed/my-simulations/${sharetoken}`,
            },
          }}
        />
      );
    }
    if (!inOrg) {
      return (
        <>
          {showRegisterPrompt && (
            <RegisterBanner
              type="virtualhuman"
              sharetoken={sharetoken}
              loggedin={loggedin}
            />
          )}
          <MobileMessage>
            <div>
              Unfortunately, it seems that you do not have access to this
              content. Please contact your administrator to resolve this.
            </div>
          </MobileMessage>
        </>
      );
    }
  }

  return (
    <>
      {showRegisterPrompt && (
        <RegisterBanner
          type="simulation"
          sharetoken={sharetoken}
          loggedin={loggedin}
        />
      )}
      <SimulationPublicPlayer simulation={simulation} sharetoken={sharetoken} />
    </>
  );
};

export default SimulationPlayerPublicPage;
